import { ThemeOptions, alpha } from '@mui/material';

// Colors

const neutral = {
  100: '#F3F4F6',
  200: '#dee0e3',
  300: '#c2c4c7',
  400: '#a1a3a9',
  500: '#7e8188',
  600: '#5e6168',
  700: '#42444d',
  800: '#2b2d37',
  900: '#1C1F28',
};

const background = {
  default: neutral[100],
  paper: '#FFFFFF',
};

const divider = '#E6E8F0';

const primary = {
  main: '#5048E5',
  light: '#828DF8',
  dark: '#3832A0',
  contrastText: '#FFFFFF',
};

const secondary = {
  main: '#10B981',
  light: '#3FC79A',
  dark: '#0B815A',
  contrastText: '#FFFFFF',
};

const success = {
  main: '#14B8A6',
  light: '#43C6B7',
  dark: '#0E8074',
  contrastText: '#FFFFFF',
};

const info = {
  main: '#2196F3',
  light: '#64B6F7',
  dark: '#0B79D0',
  contrastText: '#FFFFFF',
};

const warning = {
  main: '#FFB020',
  light: '#FFBF4C',
  dark: '#B27B16',
  // 이름·설명용: warning.main : error.main = 3 : 1 (RGB)
  readable: '#F49529',
  contrastText: '#FFFFFF',
};

const error = {
  main: '#D14343',
  light: '#DA6868',
  dark: '#922E2E',
  contrastText: '#FFFFFF',
};

const text = {
  primary: '#121828',
  secondary: '#65748B',
  disabled: 'rgba(55, 65, 81, 0.48)',
};

// COLOR
// other color
const other = {
  main: '#D1D5DB',
  outlinedBorder: '#D1D5DB',
  backdrop: 'rgba(0, 0, 0, 0.5)',
  divider: '#E6E8F0',
  snackbarBackground: '#323232',
  rating: '#FFB400',
};

// CUSTOM COLOR
// 중요도가 떨어지는 액션에 무채색 배정
const trivialAction = {
  main: text.secondary,
  light: '#FBD861',
  dark: '#AF9028',
  contrastText: neutral[900],
};

// 관심에서 벗어난 상태를 뜻함 (info/warning/error 와 동급)
const inactiveStatus = {
  main: 'rgba(0, 0, 0, 0.26)',
  light: '#FBD861',
  dark: '#AF9028',
  contrastText: '#fff',
};

// 교사 근태시 표시하는 컬러. light는 출근, dark는 퇴근쪽
const teacherClockInOut = {
  main: '#9281b2',
  light: '#9e8cbf',
  dark: '#695d80',
  contrastText: '#000',
};

const calendarBorder = {
  main: neutral[200],
  light: neutral[100],
  dark: neutral[300],
  contrastText: text.primary,
};

const cardOnPaper = {
  main: background.default,
  light: background.default,
  dark: background.paper,
  contrastText: text.primary,
};

export const lightThemeOptions: ThemeOptions = {
  components: {
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: neutral[400],
          color: neutral[100],
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          '&.MuiChip-filledDefault': {
            'backgroundColor': neutral[200],
            '& .MuiChip-deleteIcon': {
              color: neutral[400],
            },
          },
          '&.MuiChip-outlinedDefault': {
            '& .MuiChip-deleteIcon': {
              color: neutral[300],
            },
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          'scrollbarColor': `${alpha(neutral[600], 0.5)} ${alpha(neutral[200], 0.7)}`,
          'scrollbarWidth': 'thin',
          '&::-webkit-scrollbar': {
            width: 6,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(neutral[600], 0.5),
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: alpha(neutral[200], 0.7),
            borderRadius: 4,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            opacity: 1,
            color: text.secondary,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: divider,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderColor: divider,
          borderStyle: 'solid',
          borderWidth: 1,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderColor: divider,
          borderStyle: 'solid',
          borderWidth: 1,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          color: neutral[500],
        },
        track: {
          backgroundColor: neutral[400],
          opacity: 1,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${divider}`,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          'backgroundColor': neutral[200],
          '.MuiTableCell-root': {
            color: neutral[700],
          },
        },
      },
    },
  },
  palette: {
    action: {
      active: neutral[500],
      focus: 'rgba(55, 65, 81, 0.12)',
      hover: 'rgba(55, 65, 81, 0.04)',
      selected: 'rgba(55, 65, 81, 0.08)',
      disabledBackground: 'rgba(55, 65, 81, 0.12)',
      disabled: 'rgba(55, 65, 81, 0.26)',
    },
    background,
    divider,
    error,
    info,
    mode: 'light',
    neutral,
    primary,
    secondary,
    success,
    text,
    warning,
    other,
    trivialAction,
    inactiveStatus,
    teacherClockInOut,
    calendarBorder,
    cardOnPaper,
  },
  shadows: [
    'none',
    '0px 1px 1px rgba(100, 116, 139, 0.06), 0px 1px 2px rgba(100, 116, 139, 0.1)',
    '0px 1px 2px rgba(100, 116, 139, 0.12)',
    '0px 1px 4px rgba(100, 116, 139, 0.12)',
    '0px 1px 5px rgba(100, 116, 139, 0.12)',
    '0px 1px 6px rgba(100, 116, 139, 0.12)',
    '0px 2px 6px rgba(100, 116, 139, 0.12)',
    '0px 3px 6px rgba(100, 116, 139, 0.12)',
    '0px 2px 4px rgba(31, 41, 55, 0.06), 0px 4px 6px rgba(100, 116, 139, 0.12)',
    '0px 5px 12px rgba(100, 116, 139, 0.12)',
    '0px 5px 14px rgba(100, 116, 139, 0.12)',
    '0px 5px 15px rgba(100, 116, 139, 0.12)',
    '0px 6px 15px rgba(100, 116, 139, 0.12)',
    '0px 7px 15px rgba(100, 116, 139, 0.12)',
    '0px 8px 15px rgba(100, 116, 139, 0.12)',
    '0px 9px 15px rgba(100, 116, 139, 0.12)',
    '0px 10px 15px rgba(100, 116, 139, 0.12)',
    '0px 12px 22px -8px rgba(100, 116, 139, 0.25)',
    '0px 13px 22px -8px rgba(100, 116, 139, 0.25)',
    '0px 14px 24px -8px rgba(100, 116, 139, 0.25)',
    '0px 10px 10px rgba(31, 41, 55, 0.04), 0px 20px 25px rgba(31, 41, 55, 0.1)',
    '0px 25px 50px rgba(100, 116, 139, 0.25)',
    '0px 25px 50px rgba(100, 116, 139, 0.25)',
    '0px 25px 50px rgba(100, 116, 139, 0.25)',
    '0px 25px 50px rgba(100, 116, 139, 0.25)',
  ],
};
